import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  computed,
  inject,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { clone } from "lodash-es";
import { StudentService } from "../../shared/services/student.service";
import { LocaleService } from "../../shared/services/locale.service";
import { AlertService } from "../../shared/services/alert.service";
import {
  faShare,
  faLock,
  faLockOpen,
  faCopy,
  faCalendar,
  faEnvelope,
  faGlobe,
  faLanguage,
  faMapMarker,
  faPhone,
  faCheck,
  faEye,
  faEyeSlash,
  faBirthdayCake,
  faPencilAlt,
  faSync,
  faSave,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  CvSettings,
  defaultCvSettings,
  CV_SECTIONS,
} from "../../model/model.cv-settings";
import {
  ModelStudentprofile,
  studentprofilePlaceholder,
} from "../../model/model.studentprofile";
import { catchError, first, map } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { InterestService } from "src/app/shared/services/interest.service";
import { ActivatedRoute, Router } from "@angular/router";
import { ProfileTypeUtils } from "../../shared/utils/student-profile.utils";
import { StudentProfileService } from "../../api/services/student-profile.service";
import {
  BaseStudentProfile,
  StudentProfile,
  PublicStudentProfile,
  StudentProfileDna,
  ChangeDnaData,
} from "../../api/models";

interface DnaState {
  isLoaded: boolean;
  isLoading: boolean;
  currentDna: {
    dnaData: ChangeDnaData | undefined;
    id: string;
    timestamp: string;
  };
  dnaResults: any[];
  dnaResultsAll: any[];
}

@Component({
  selector: "app-student-resume",
  templateUrl: "./student-resume.component.html",
  styleUrls: ["./student-resume.component.scss"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudentResumeComponent implements OnInit {
  private studentProfileService = inject(StudentProfileService);
  private readonly destroyRef = inject(DestroyRef);

  // Loading + identity
  readonly isLoading = signal(false);
  avatarPlaceholder = studentprofilePlaceholder.avatarPlaceholder;
  publicUserId: string | null = null;

  // Core data — signals. `entity` is loosely typed because the runtime
  // value is a hybrid of API response (StudentProfile / PublicStudentProfile)
  // and UI-specific shape (`ModelStudentprofile`); the upstream model types
  // don't fully cover every field used in this template.
  readonly entity = signal<any>(undefined);
  readonly originalEntity = signal<any>(undefined);
  readonly cvSettings = signal<CvSettings>({ ...defaultCvSettings });
  readonly dna = signal<DnaState>({
    isLoaded: false,
    isLoading: false,
    currentDna: { dnaData: undefined, id: "", timestamp: "" },
    dnaResults: [],
    dnaResultsAll: [],
  });

  // Public/private CV state
  readonly publicLink = signal("");
  readonly isPublic = signal(false);

  // Edit mode state
  readonly isEditMode = signal(false);
  readonly hasEdits = signal(false);

  // Derived state
  readonly aboutMeText = computed(() => {
    const edited = this.cvSettings().editedAboutMe;
    if (edited) return edited;
    return this.entity()?.aiGeneratedProfileSummary || "";
  });
  readonly hasAboutMe = computed(
    () =>
      !!this.entity()?.aiGeneratedProfileSummary ||
      !!this.cvSettings().editedAboutMe
  );

  // Icons
  faShare = faShare;
  faLock = faLock;
  faLockOpen = faLockOpen;
  faCopy = faCopy;
  faMapMarker = faMapMarker;
  faPhone = faPhone;
  faEnvelope = faEnvelope;
  faGlobe = faGlobe;
  faCalendar = faCalendar;
  faLanguage = faLanguage;
  faCheck = faCheck;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faBirthdayCake = faBirthdayCake;
  faPencilAlt = faPencilAlt;
  faSync = faSync;
  faSave = faSave;
  faTimes = faTimes;

  // Constants
  readonly ProfileTypeUtils = ProfileTypeUtils;
  readonly CV_SECTIONS = CV_SECTIONS;
  readonly languageLevels = {
    0: "Poor",
    1: "Basic",
    2: "Intermediate",
    3: "Good",
    4: "Fluent",
    5: "Native",
  };

  cvSummary =
    "A motivated and enthusiastic individual seeking opportunities to grow and contribute...";

  dnaLoaded = new EventEmitter<void>();

  trackById = (_index: number, item: { id?: string | null }): string =>
    item.id ?? `index-${_index}`;
  trackByIndex = (index: number): number => index;

  constructor(
    public studentService: StudentService,
    public interestService: InterestService,
    public locale: LocaleService,
    private alertService: AlertService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.publicUserId = this.route.snapshot.paramMap.get("id");
    this.loadStudentCV();
  }

  private isValidUUID(uuid: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      uuid
    );
  }

  loadStudentCV() {
    this.isLoading.set(true);

    if (this.publicUserId) {
      if (!this.isValidUUID(this.publicUserId)) {
        this.router.navigate(["/errors/404"]);
        return;
      }

      this.studentProfileService
        .getStudentProfileCv({ studentId: this.publicUserId })
        .pipe(
          first(),
          catchError((error) => {
            this.router.navigate(["/errors/404"]);
            return this.handleError<PublicStudentProfile | undefined>(
              "getStudentProfileCv",
              undefined
            )(error);
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((response) => {
          this.isLoading.set(false);
          if (response) {
            const formatted = this.normalizeProfile(
              response as unknown as ModelStudentprofile
            );
            this.entity.set(formatted);

            this.dna.update((d) => ({
              ...d,
              currentDna: {
                ...d.currentDna,
                dnaData: response.currentDna?.dnaData
                  ? JSON.parse(response.currentDna.dnaData)
                  : d.currentDna.dnaData,
                timestamp: response.dnaTimestamp ?? d.currentDna.timestamp,
              },
            }));

            this.isPublic.set(true);
            this.publicLink.set(window.location.href);
            this.cvSettings.set(
              this.convertProfileToCvSettings(response) ?? {
                ...defaultCvSettings,
              }
            );
            this.loadDNA();
          }
        });
    } else {
      this.studentProfileService
        .getCurrentStudentProfile()
        .pipe(
          first(),
          catchError(
            this.handleError<StudentProfile | undefined>(
              "getCurrentStudentProfile",
              undefined
            )
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((response) => {
          this.isLoading.set(false);
          if (response) {
            const formatted = this.normalizeProfile(
              response as unknown as ModelStudentprofile
            );
            this.entity.set(formatted);
            this.originalEntity.set(clone(formatted));
            this.loadPublicStatus();
            this.loadDNA();
            this.loadCvSettings();
          }
        });
    }
  }

  loadPublicStatus() {
    const current = this.entity();
    if (!current?.id) return;

    this.studentProfileService
      .getStudentProfileCv({ studentId: current.id })
      .pipe(
        first(),
        catchError(
          this.handleError<PublicStudentProfile | undefined>(
            "getStudentProfileCv",
            undefined
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        if (response) {
          this.isPublic.set(true);
          this.publicLink.set(
            `${window.location.origin}/public-cv/${current.id}`
          );
          // Merge AI summary into entity so the "About me" section reacts
          this.entity.update((e) =>
            e
              ? ({
                  ...e,
                  aiGeneratedProfileSummary: response.aiGeneratedProfileSummary,
                } as ModelStudentprofile)
              : e
          );
        } else {
          this.isPublic.set(false);
          this.publicLink.set("");
        }
      });
  }

  loadDNA() {
    const current = this.entity();
    if (current?.id && !this.dna().isLoaded) {
      this.loadStudentprofileCurrentDNA(current.id);
      this.loadStudentprofileDNA(current.id);
      this.dna.update((d) => ({ ...d, isLoaded: true }));
    }
  }

  loadStudentprofileCurrentDNA(id: string) {
    this.dna.update((d) => ({ ...d, isLoading: true }));
    this.studentProfileService
      .getCurrentStudentDna({ profileId: id })
      .pipe(
        first(),
        catchError(
          this.handleError<StudentProfileDna | undefined>(
            "getCurrentStudentDna",
            undefined
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.dna.update((d) => ({
          ...d,
          isLoading: false,
          currentDna:
            result && result.dnaData
              ? {
                  dnaData: JSON.parse(result.dnaData),
                  id: result.id || "",
                  timestamp: result.timestamp || "",
                }
              : d.currentDna,
        }));
        if (result && result.dnaData) {
          this.dnaLoaded.emit();
        }
      });
  }

  loadStudentprofileDNA(id: string) {
    this.studentProfileService
      .getStudentDnaHistory({ profileId: id })
      .pipe(
        first(),
        catchError(
          this.handleError<StudentProfileDna[]>("getStudentDnaHistory", [])
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        if (result && Array.isArray(result) && result.length > 0) {
          const reversed = clone(result).reverse();
          reversed.forEach((item: any) => {
            if (item.dnaData) {
              item.dnaData = JSON.parse(item.dnaData);
            }
          });
          this.dna.update((d) => ({
            ...d,
            dnaResultsAll: clone(reversed),
            dnaResults: reversed.slice(0, 5),
          }));
        }
      });
  }

  /**
   * Format raw API profile into a renderable shape (Date instances, sorted
   * collections). Returns a NEW object so signal updates trigger CD.
   */
  private normalizeProfile(raw: ModelStudentprofile): ModelStudentprofile {
    const formatted: any = { ...raw };

    if (formatted.birthDate) {
      formatted.birthDate = new Date(formatted.birthDate);
    }

    const formatCollection = (items: any[] | undefined) =>
      items?.map((item) => ({
        ...item,
        startDate: item.startDate ? new Date(item.startDate) : item.startDate,
        endDate: item.endDate ? new Date(item.endDate) : item.endDate,
      })) ?? items;

    formatted.educations = formatCollection(formatted.educations);
    formatted.workExperiences = formatCollection(formatted.workExperiences);
    formatted.foreignExperiences = formatCollection(formatted.foreignExperiences);

    const sortByStartDateDesc = (a: any, b: any) =>
      new Date(b.startDate).getTime() - new Date(a.startDate).getTime();

    formatted.educations?.sort(sortByStartDateDesc);
    formatted.workExperiences?.sort(sortByStartDateDesc);
    formatted.foreignExperiences?.sort(sortByStartDateDesc);

    return formatted;
  }

  togglePublicCV() {
    const current = this.entity();
    if (!current?.id) return;

    this.studentProfileService
      .updatePublicStatusStudentProfileCv({
        studentId: current.id,
        shouldBePublic: !this.isPublic(),
      })
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isPublic.update((v) => !v);
          if (this.isPublic()) {
            this.publicLink.set(
              `${window.location.origin}/public-cv/${current.id}`
            );
            this.alertService.success("CV is nu publiek");
          } else {
            this.publicLink.set("");
            this.alertService.success("CV is nu niet meer publiek");
          }
        },
        error: () => {
          this.alertService.error(
            "Er is iets misgegaan bij het wijzigen van de CV zichtbaarheid"
          );
        },
      });
  }

  getCircleArray(level: number): boolean[] {
    return Array.from({ length: 6 }, (_, i) => i < level);
  }

  getLanguageLevel(level: number): string {
    return (
      this.languageLevels[level as keyof typeof this.languageLevels] ||
      "Not Specified"
    );
  }

  copyPublicLink() {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(this.publicLink())
        .then(() =>
          this.alertService.success("Link is gekopieerd naar het klembord")
        )
        .catch(() => this.fallbackCopyLink());
    } else {
      this.fallbackCopyLink();
    }
  }

  private fallbackCopyLink(): void {
    const el = document.createElement("textarea");
    el.value = this.publicLink();
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    this.alertService.success("Link is gekopieerd naar het klembord");
  }

  getFormattedLocation(item: any): string {
    const parts = [];
    if (item.city) parts.push(item.city);
    if (item.country) parts.push(this.locale.getCountryName(item.country));
    return parts.join(", ");
  }

  // ============ CV Edit Mode Methods ============

  toggleEditMode(): void {
    this.isEditMode.update((v) => !v);
    if (this.isEditMode()) {
      const current = this.entity();
      if (current) this.originalEntity.set(clone(current));
    }
  }

  cancelEditMode(): void {
    this.isEditMode.set(false);
    this.loadCvSettings();
  }

  isSectionHidden(section: string): boolean {
    return this.cvSettings().hiddenSections.includes(section);
  }

  toggleSectionVisibility(section: string): void {
    this.cvSettings.update((s) => {
      const isHidden = s.hiddenSections.includes(section);
      return {
        ...s,
        hiddenSections: isHidden
          ? s.hiddenSections.filter((x) => x !== section)
          : [...s.hiddenSections, section],
      };
    });
  }

  isItemHidden(
    type: "education" | "workExperience" | "foreignExperience",
    id: string
  ): boolean {
    const s = this.cvSettings();
    switch (type) {
      case "education":
        return s.hiddenEducations.includes(id);
      case "workExperience":
        return s.hiddenWorkExperiences.includes(id);
      case "foreignExperience":
        return s.hiddenForeignExperiences.includes(id);
      default:
        return false;
    }
  }

  toggleItemVisibility(
    type: "education" | "workExperience" | "foreignExperience",
    id: string
  ): void {
    this.cvSettings.update((s) => {
      const key =
        type === "education"
          ? "hiddenEducations"
          : type === "workExperience"
          ? "hiddenWorkExperiences"
          : "hiddenForeignExperiences";
      const current = s[key];
      const exists = current.includes(id);
      return {
        ...s,
        [key]: exists ? current.filter((x) => x !== id) : [...current, id],
      };
    });
  }

  /**
   * @deprecated kept for backward compatibility — prefer the `aboutMeText`
   * computed signal directly in templates.
   */
  getAboutMeText(): string {
    return this.aboutMeText();
  }

  updateAboutMe(text: string): void {
    this.cvSettings.update((s) => ({ ...s, editedAboutMe: text }));
  }

  restoreAiSummary(): void {
    this.cvSettings.update((s) => ({ ...s, editedAboutMe: undefined }));
  }

  saveCvSettings(): void {
    const current = this.entity();
    if (!current?.id) return;

    const apiFields = this.convertCvSettingsToApiFields(this.cvSettings());

    this.studentProfileService
      .updateStudentProfile({
        id: current.id,
        body: {
          ...this.buildProfileBody(),
          hiddenCvFields: apiFields.hiddenCvFields,
          userGeneratedProfileSummary: apiFields.userGeneratedProfileSummary,
        },
      })
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.hasEdits.set(true);
          this.isEditMode.set(false);
          this.alertService.success("CV wijzigingen opgeslagen");
        },
        error: () => {
          this.alertService.error(
            "Er is iets misgegaan bij het opslaan van de CV wijzigingen"
          );
        },
      });
  }

  loadCvSettings(): void {
    const current = this.entity();
    if (!current?.id) return;

    this.studentProfileService
      .getStudentProfile({ id: current.id })
      .pipe(
        first(),
        map((profile: StudentProfile) =>
          this.convertProfileToCvSettings(profile)
        ),
        catchError(this.handleError("getStudentProfile", null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result: CvSettings | null) => {
        if (result) {
          this.cvSettings.set(result);
          this.hasEdits.set(true);
        } else {
          this.cvSettings.set({ ...defaultCvSettings });
          this.hasEdits.set(false);
        }
      });
  }

  regenerateCv(): void {
    const current = this.entity();
    if (!current?.id) return;

    if (
      !confirm(
        "Weet je zeker dat je het CV wilt herstellen? Alle wijzigingen worden verwijderd."
      )
    ) {
      return;
    }

    this.studentProfileService
      .updateStudentProfile({
        id: current.id,
        body: {
          ...this.buildProfileBody(),
          hiddenCvFields: [],
          userGeneratedProfileSummary: null,
        },
      })
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.cvSettings.set({ ...defaultCvSettings });
          this.hasEdits.set(false);
          this.isEditMode.set(false);
          this.alertService.success("CV is hersteld naar origineel");
        },
        error: () => {
          this.alertService.error(
            "Er is iets misgegaan bij het herstellen van het CV"
          );
        },
      });
  }

  /**
   * Build a clean profile body with only scalar fields for the PUT endpoint.
   * Excludes collections (workExperiences, educations, etc.) that are managed
   * by their own sub-resource endpoints and may contain dates the API can't deserialize.
   */
  private buildProfileBody(): StudentProfile {
    const src = this.originalEntity() as any;
    return {
      id: src.id,
      firstName: src.firstName,
      lastName: src.lastName,
      infix: src.infix,
      phoneNumber: src.phoneNumber,
      birthDate: src.birthDate,
      homeAddress: src.homeAddress,
      emailAddress: src.emailAddress,
      profileType: src.profileType,
      lookingFor: src.lookingFor,
      allowEmailNotifications: src.allowEmailNotifications,
      isSubscribedToNewsletter: src.isSubscribedToNewsletter,
      hasMediaConsent: src.hasMediaConsent ?? false,
      hasAcceptedGdprPolicy: src.hasAcceptedGdprPolicy ?? false,
      hasDataUsageConsent: src.hasDataUsageConsent ?? false,
      referenceCode: src.referenceCode || null,
      preferredLanguage: src.preferredLanguage || null,
      generateDnaDataAutomatically: src.generateDnaDataAutomatically,
      currentDnaId: src.currentDnaId,
      hiddenCvFields: src.hiddenCvFields,
      userGeneratedProfileSummary: src.userGeneratedProfileSummary,
      aiGeneratedProfileSummary: src.aiGeneratedProfileSummary,
    } as StudentProfile;
  }

  // ============ CV Settings Conversion Utilities ============

  private convertProfileToCvSettings(
    profile: BaseStudentProfile
  ): CvSettings | null {
    if (!profile) return null;

    const hiddenCvFields = profile.hiddenCvFields || [];
    const settings: CvSettings = {
      hiddenSections: hiddenCvFields.filter((f) => !f.includes(":")),
      hiddenEducations: this.extractItemIds(hiddenCvFields, "education"),
      hiddenWorkExperiences: this.extractItemIds(
        hiddenCvFields,
        "workExperience"
      ),
      hiddenForeignExperiences: this.extractItemIds(
        hiddenCvFields,
        "foreignExperience"
      ),
      editedAboutMe: profile.userGeneratedProfileSummary || undefined,
    };

    const hasSettings =
      settings.hiddenSections.length > 0 ||
      settings.hiddenEducations.length > 0 ||
      settings.hiddenWorkExperiences.length > 0 ||
      settings.hiddenForeignExperiences.length > 0 ||
      settings.editedAboutMe;

    return hasSettings ? settings : null;
  }

  private convertCvSettingsToApiFields(settings: CvSettings): {
    hiddenCvFields: string[];
    userGeneratedProfileSummary: string | null;
  } {
    const hiddenCvFields: string[] = [
      ...settings.hiddenSections,
      ...settings.hiddenEducations.map((id) => `education:${id}`),
      ...settings.hiddenWorkExperiences.map((id) => `workExperience:${id}`),
      ...settings.hiddenForeignExperiences.map(
        (id) => `foreignExperience:${id}`
      ),
    ];

    return {
      hiddenCvFields,
      userGeneratedProfileSummary: settings.editedAboutMe || null,
    };
  }

  private extractItemIds(hiddenFields: string[], prefix: string): string[] {
    return hiddenFields
      .filter((f) => f.startsWith(`${prefix}:`))
      .map((f) => f.substring(prefix.length + 1));
  }

  private handleError<T>(_operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return of(result as T);
    };
  }
}
